/*
  Shift modular sections · video behaviour
  - [data-sr-video][data-autoplay]: plays muted while at least half in view, pauses when out.
    Never autoplays when the visitor prefers reduced motion.
  - [data-sr-toggle]: play / pause button; a manual pause sticks until the visitor presses play.
  - A tap or click anywhere on the video does the same (links and buttons over it keep their own job).
  - Only the visible <video> (desktop or mobile) is touched.
  - Posters below the top of the page wait in data-sr-poster (sr-video.liquid) and are set
    once the slot is within a screen of view, on the version shown at this width only.
  Safe to include from several sections: it initialises each media slot once.
*/
(function () {
  if (window.SRMedia) {
    window.SRMedia.init();
    return;
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var wide = window.matchMedia('(min-width: 750px)');

  // The video shown at this width (sr-base.css swaps .sr-media__mobile / __desktop at 750px).
  // Decided by the width, not by layout, so it also works while the slot itself is hidden.
  function shownVideo(slot) {
    var mobile = slot.querySelector('.sr-media__mobile video');
    var desktop = slot.querySelector('.sr-media__desktop video');
    return (!wide.matches && mobile) || desktop || mobile;
  }

  function loadPoster(video) {
    if (!video) return;
    var src = video.getAttribute('data-sr-poster');
    if (!src) return;
    video.setAttribute('poster', src);
    // Behind the video too, for Safari's gap between poster and first frame (sr-base.css)
    video.style.backgroundImage = 'url("' + src + '")';
    video.removeAttribute('data-sr-poster');
  }

  var nearSlots = [];
  function slotNear(slot) {
    loadPoster(shownVideo(slot));
    if (nearSlots.indexOf(slot) === -1) nearSlots.push(slot);
  }

  // Slides off to the side of a carousel never count as near (their track clips them),
  // so a sideways scroller is watched instead and brings all its slots in at once.
  function scrollerOf(slot) {
    for (var el = slot.parentElement; el && el !== document.body; el = el.parentElement) {
      var x = getComputedStyle(el).overflowX;
      if (x === 'auto' || x === 'scroll') return el;
    }
    return null;
  }

  var posterObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var target = entry.target;
              if (target.__srSlots) {
                target.__srSlots.forEach(slotNear);
                posterObserver.unobserve(target);
              } else {
                slotNear(target);
              }
            });
          },
          { rootMargin: '100% 0px' }
        )
      : null;

  function watchPoster(slot) {
    if (!posterObserver) {
      slot.querySelectorAll('video').forEach(loadPoster);
      return;
    }
    var scroller = scrollerOf(slot);
    if (!scroller) {
      posterObserver.observe(slot);
      return;
    }
    if (!scroller.__srSlots) {
      scroller.__srSlots = [];
      posterObserver.observe(scroller);
    }
    scroller.__srSlots.push(slot);
  }

  // Crossing 750px shows the other version: give it its poster too if the slot is near
  function onWidthChange() {
    nearSlots.forEach(function (slot) { loadPoster(shownVideo(slot)); });
  }
  if (wide.addEventListener) wide.addEventListener('change', onWidthChange);
  else if (wide.addListener) wide.addListener(onWidthChange);

  function activeVideo(slot) {
    var videos = slot.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) {
      if (videos[i].offsetParent !== null) return videos[i];
    }
    return videos[0] || null;
  }

  function sync(slot) {
    var toggle = slot.querySelector('[data-sr-toggle]');
    var video = activeVideo(slot);
    if (!toggle || !video) return;
    var playing = !video.paused;
    toggle.toggleAttribute('data-playing', playing);
    toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }

  function play(slot) {
    var video = activeVideo(slot);
    if (!video) return;
    slot.querySelectorAll('video').forEach(function (v) {
      if (v !== video && !v.paused) v.pause();
    });
    loadPoster(video);
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  function pause(slot) {
    slot.querySelectorAll('video').forEach(function (v) {
      v.pause();
    });
  }

  // A visitor's play / pause: a pause sticks, so scrolling back doesn't restart the video
  function toggle(slot) {
    var video = activeVideo(slot);
    if (!video) return;
    if (video.paused) {
      slot.removeAttribute('data-user-paused');
      play(slot);
    } else {
      slot.setAttribute('data-user-paused', '');
      pause(slot);
    }
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var slot = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                if (!slot.hasAttribute('data-user-paused') && !reduceMotion.matches) play(slot);
              } else {
                pause(slot);
              }
            });
          },
          { threshold: [0, 0.5] }
        )
      : null;

  function init(root) {
    (root || document).querySelectorAll('[data-sr-video]:not([data-sr-ready])').forEach(function (slot) {
      slot.setAttribute('data-sr-ready', '');
      slot.querySelectorAll('video').forEach(function (v) {
        v.addEventListener('play', function () { sync(slot); });
        v.addEventListener('pause', function () { sync(slot); });
      });
      // The toggle button is inside the slot, so its click lands here too
      slot.addEventListener('click', function (e) {
        var control = e.target.closest('a, button, input, select, textarea, label');
        if (control && !control.hasAttribute('data-sr-toggle')) return;
        toggle(slot);
      });
      if (slot.hasAttribute('data-autoplay') && observer) observer.observe(slot);
      if (slot.querySelector('video[data-sr-poster]')) watchPoster(slot);
    });
  }

  window.SRMedia = { init: init, play: play, pause: pause };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
